"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import { calcMulch, type MulchResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const SHAPE_OPTIONS = [
  { value: "rectangular", label: "Rectangular / L-shaped bed" },
  { value: "circular", label: "Circular / Round bed" },
];

const MATERIAL_OPTIONS = [
  { value: "mulch", label: "Mulch (recommended depth: 2–3 in)" },
  { value: "gravel", label: "Gravel / Stone (recommended: 2–4 in)" },
  { value: "topsoil", label: "Topsoil / Garden Soil (recommended: 4–6 in)" },
  { value: "sand", label: "Sand (recommended: 2–3 in)" },
];

const DEPTH_HINTS: Record<string, string> = {
  mulch:    "Recommended: 2–3 inches to suppress weeds",
  gravel:   "Recommended: 2–4 inches for walkways, 4–6 in for driveways",
  topsoil:  "Recommended: 4–6 inches for new gardens",
  sand:     "Recommended: 2–3 inches for playgrounds or leveling",
};

export default function CalculatorClient() {
  const [shape, setShape] = useState<"rectangular" | "circular">("rectangular");
  const [material, setMaterial] = useState("mulch");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [diameter, setDiameter] = useState("");
  const [depth, setDepth] = useState("3");
  const [bagVolume, setBagVolume] = useState("2");
  const [result, setResult] = useState<MulchResult | null>(null);

  function handleCalculate() {
    const res = calcMulch({
      shape,
      lengthFt: parseFloat(length) || 0,
      widthFt: parseFloat(width) || 0,
      diameterFt: parseFloat(diameter) || 0,
      depthIn: parseFloat(depth) || 3,
      bagVolumeCuFt: parseFloat(bagVolume) || 2,
    });
    setResult(res);
  }

  return (
    <CalculatorShell
      name="Mulch & Gravel Calculator"
      description="Calculate cubic yards or bags of mulch, gravel, topsoil, or sand needed for any landscaping project."
      icon="mulch"
      category="diy"
      accentColor="#22c55e"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Estimates account for standard waste factor. Organic materials settle 5–15% after installation; non-flat terrain may require additional ordering."
      proTip="Buy 1–2 extra bags for annual touch-ups in high-washout areas near downspouts."
      inputsPanel={
        <div className="space-y-4">
          <SelectField id="mulch-material" label="Material Type" value={material} onChange={(e) => { setMaterial(e.target.value); setResult(null); }} options={MATERIAL_OPTIONS} />
          <SelectField id="mulch-shape" label="Bed Shape" value={shape} onChange={(e) => { setShape(e.target.value as typeof shape); setResult(null); }} options={SHAPE_OPTIONS} />

          {shape === "rectangular" ? (
            <div className="grid grid-cols-2 gap-3">
              <InputField id="mulch-length" label="Length" unit="ft" type="number" min="0" step="0.5" placeholder="20" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
              <InputField id="mulch-width" label="Width" unit="ft" type="number" min="0" step="0.5" placeholder="8" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
            </div>
          ) : (
            <InputField id="mulch-diameter" label="Bed Diameter" unit="ft" type="number" min="0" step="0.5" placeholder="10" value={diameter} onChange={(e) => { setDiameter(e.target.value); setResult(null); }} />
          )}

          <InputField
            id="mulch-depth"
            label="Desired Depth"
            unit="in"
            type="number"
            min="0"
            step="0.5"
            placeholder="3"
            value={depth}
            onChange={(e) => { setDepth(e.target.value); setResult(null); }}
            hint={DEPTH_HINTS[material]}
          />
          <InputField
            id="mulch-bag-volume"
            label="Volume per Bag"
            unit="cu ft"
            type="number"
            min="0"
            step="0.5"
            placeholder="2"
            value={bagVolume}
            onChange={(e) => { setBagVolume(e.target.value); setResult(null); }}
            hint="Check your bag label — most bagged mulch is 2 cu ft per bag"
          />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <ResultCard label="Cubic Yards (+ 10% buffer)" value={result.cubicYardsWithBuffer} unit="yd³" highlight size="lg" sub={`${result.cubicYards} yd³ exact · Buy in bulk for >3 yd³`} />
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Area Covered" value={result.areaSqFt.toLocaleString()} unit="sq ft" accent />
              <ResultCard label="Cubic Feet" value={result.cubicFt} unit="ft³" accent />
            </div>
            {result.bagsWithBuffer !== undefined && (
              <ResultCard label="Bags Needed (+ 10% buffer)" value={result.bagsWithBuffer} unit="bags" highlight sub={`${result.bagsNeeded} bags exact · ${bagVolume} cu ft per bag`} />
            )}
            {/* Quick reference table */}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Coverage Reference (1 cubic yard covers…)
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { depth: "1 inch", coverage: "324 sq ft" },
                  { depth: "2 inches", coverage: "162 sq ft" },
                  { depth: "3 inches", coverage: "108 sq ft" },
                  { depth: "4 inches", coverage: "81 sq ft" },
                ].map((row, i) => {
                  const isSelected = row.depth === `${depth} inch` || row.depth === `${depth} inches`;
                  return (
                    <div
                      key={i}
                      className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                        isSelected 
                          ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30" 
                          : "border-l-3 border-l-transparent"
                      }`}
                    >
                      <span className={`text-[12.5px] font-sans ${isSelected ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                        At {row.depth} {isSelected && <span className="text-[9.5px] text-[var(--accent)] font-mono ml-2">(Selected)</span>}
                      </span>
                      <span className={`font-mono text-[13px] ${isSelected ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>{row.coverage}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Mulch & Gravel Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            For home landscaping, matching your organic mulch or mineral gravel to your design goals is crucial. Double-shredded hardwood mulch locks together well on slopes, while pine bark nuggets last longer but tend to float away in heavy rain. An optimal mulch layer depth is <strong>{CALCULATOR_SPECS.mulch.buyingGuide.optimalMulchDepthRange.value}</strong> for effective weed suppression and soil moisture retention. If you&apos;re selecting stones, pea gravel is standard at a <strong>{CALCULATOR_SPECS.mulch.buyingGuide.peaGravelSizeStandard.value}</strong> size, providing a clean texture for walkways, patios, and planter beds. If you are restoring nearby turf, check our guide on <Link href="/articles/cost-to-sod-or-reseed-lawn/" className="text-[var(--accent)] hover:underline font-semibold">lawn sodding and reseeding</Link>.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/mulch_material_guide.webp" alt="Lush planter beds containing dark shredded wood mulch and light gravel path" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/mulch_site_prep.webp" alt="Winding garden bed containing clean soil with weed barrier landscape fabric laid down" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Mulch & Gravel Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Proper bed preparation prevents weeds and soil compaction. Remove all existing weeds, roots, and debris, then grade the soil to prevent standing water. Lay a commercial-grade, non-woven landscape fabric (typically weighing <strong>{CALCULATOR_SPECS.mulch.sitePrep.geotextileFabricWeight.value}</strong>) under gravel to separate the stones from the dirt. For walks or parking areas near concrete or asphalt, refer to our <Link href="/articles/driveway-sealing-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">driveway sealing cost guide</Link> to protect the surrounding layout. When spreading organic mulch, always leave a 2-inch gap around tree trunks and shrub bases; piling mulch directly against bark (known as &apos;mulch volcanoes&apos;) traps moisture and invites rot, fungi, and insect infestations.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.mulch.industryStandards.fireSafetyDistance.standardCode}
        sourceName={CALCULATOR_SPECS.mulch.industryStandards.fireSafetyDistance.sourceName}
        sourceUrl={CALCULATOR_SPECS.mulch.industryStandards.fireSafetyDistance.sourceUrl}
        paragraphs={[
          <p key="p1">
            Dry organic mulches, such as shredded pine bark, wood chips, and straw, represent significant ignition hazards when placed close to building foundations. Under fire prevention regulations established by <b>{CALCULATOR_SPECS.mulch.industryStandards.fireSafetyDistance.standardCode}</b>, home builders and landscape designers must maintain a non-combustible safety barrier of at least <b>{CALCULATOR_SPECS.mulch.industryStandards.fireSafetyDistance.value} inches</b> between organic mulch and combustible exterior building materials. Embers from wildfires or discarded smoking materials can easily ignite dry mulch, where the fire can smolder unnoticed before spreading to wood siding or deck structures.
          </p>,
          <p key="p2">
            To address this safety concern while maintaining a clean look, designers fill the 18-inch perimeter buffer with non-combustible aggregate materials like river rocks, pea gravel, or volcanic stone. Organic mulches are kept outside this zone. Additionally, when applying organic mulch in planting beds, gardeners should avoid piling it directly against the stems or trunks of plants. This practice—often termed &quot;mulch volcanism&quot;—traps moisture, cuts off oxygen to the root flare, and creates a damp environment that invites fungal rot and wood-boring pests.
          </p>
        ]}
        keyRequirements={[
          "Maintain a minimum 18-inch non-combustible buffer from combustible siding.",
          "Use non-combustible materials like river rock or gravel within the safety buffer.",
          "Avoid organic mulches like wood chips or straw in the immediate foundation zone.",
          "Keep organic mulch at least 2 inches away from plant stems and tree trunks."
        ]}
      />

      <FaqSection calculatorId="mulch" />
      <RelatedCalculators calculatorId="mulch" />
    </CalculatorShell>
  );
}
